/**
 * The audio library's pure helpers. What a library row means in words, which is what gets embedded and
 * therefore what a prompt can find.
 */
import { z } from 'zod';
import { ASSET_NAME_MAX_CHARS, AUDIO_WAVEFORM_POINTS } from './constants.js';
import { MUSIC_GENRES, MUSIC_MOODS, SFX_CATEGORIES, SFX_SETTINGS } from './audio-taxonomy.js';
import { AudioKind } from './media.js';
import { matchScore } from './priorities.js';
/** How each kind reads in the embedded text, so "sound effect" and "music" separate in vector space. */
const KIND_WORDS = {
    sfx: 'sound effect',
    bed: 'music bed',
    track: 'music track',
    voice: 'voiceover',
};
/**
 * The one string a library row is embedded from. The description leads because it is the sentence a
 * person would say ("egg cracking on a steel bowl rim"); the name follows, because on an imported pack
 * it carries the folder taxonomy the importer put there; artist and title carry a track; tags close.
 * A row with nothing but a junk filename still embeds, it just embeds badly, which is the honest result.
 */
export function audioEmbedText(row) {
    const credit = row.kind === 'voice'
        ? (row.artist ? `voiced by ${row.artist}` : null)
        : row.title && row.artist ? `${row.title} by ${row.artist}` : row.title || row.artist || null;
    // The filing words sit beside the description, because they are what a person asks for: "upbeat pop",
    // "a kitchen sound", "trending audio".
    const filed = [
        row.trending ? 'trending viral audio' : null,
        row.moods?.length ? `${row.moods.join(', ')} mood` : null,
        row.genres?.length ? row.genres.join(', ') : null,
        row.sfxCategory ?? null,
        row.setting ? `${row.setting} setting` : null,
    ].filter(Boolean).join(', ');
    const parts = [
        KIND_WORDS[row.kind],
        row.description?.trim() || null,
        filed || null,
        row.name.trim() || null,
        credit,
        row.publisher ? `from ${row.publisher}` : null,
        row.tags?.length ? row.tags.join(', ') : null,
    ];
    return parts.filter((p) => Boolean(p)).join(' · ');
}
/**
 * Folder names that say nothing: a pack's own wrapper, a vendor's stamp, a format note. Dropping them
 * keeps "Foley/Glass" from becoming "samples, wav, 24bit, foley, glass".
 */
const PATH_NOISE = new Set([
    'samples', 'sample', 'sounds', 'sound', 'audio', 'wav', 'wavs', 'mp3', 'mp3s', 'aiff', 'flac',
    'pack', 'packs', 'library', 'files', 'exports', 'export', 'bounced', 'renders', 'misc',
    '16bit', '24bit', '32bit', '44100', '48000', '96khz', '44khz', '48khz', 'mono', 'stereo', 'new', 'final',
]);
/** Words in a folder name that mean the pack is effects rather than music. */
const EFFECT_WORDS = new Set(['sfx', 'fx', 'foley', 'effect', 'effects', 'oneshot', 'oneshots', 'one-shot', 'hits', 'impacts']);
/** A path segment or filename split into plain lowercase words: "bottle_clink_03" -> bottle, clink. */
function words(segment) {
    return segment
        .replace(/\.[^.]+$/, '')
        .split(/[^a-zA-Z0-9]+/)
        .map((w) => w.trim().toLowerCase())
        // A bare number is a take number, never a tag.
        .filter((w) => w.length > 1 && !/^\d+$/.test(w));
}
/**
 * The tags a dropped file's folders already carry. A sample pack ships its own taxonomy
 * ("Foley/Glass/bottle_clink_03.wav" is foley and glass) and reading it costs nothing, so it happens
 * before any AI listens to anything (Dax, 2026-09-20). The filename itself is deliberately NOT tagged:
 * it becomes the row's name, and tagging it again would just repeat the name in the embedding.
 */
export function audioTagsFromPath(path) {
    if (!path)
        return [];
    const segments = path.split('/').filter(Boolean);
    // The last segment is the file; everything above it is the taxonomy.
    const folders = segments.slice(0, -1);
    const out = [];
    for (const folder of folders) {
        for (const w of words(folder)) {
            if (PATH_NOISE.has(w) || out.includes(w))
                continue;
            out.push(w);
        }
    }
    return out;
}
/** Whether a dropped file's folders say it is an effects pack rather than music. */
export function pathSaysEffect(path) {
    return audioTagsFromPath(path).some((t) => EFFECT_WORDS.has(t));
}
/**
 * What one listen tells us about a sample. The third rung of the naming ladder, reached only when the
 * filename was junk: a paid pack's files never come here, a folder of hand-made rips all do.
 */
export const AudioDescription = z.object({
    /** A short human file name, 3 to 6 plain words: what the row is called from now on. */
    name: z.string().min(1).max(ASSET_NAME_MAX_CHARS),
    /** The sentence a person would say to find it again. */
    description: z.string().max(300),
    /** Two to five plain lowercase words: material, action, texture. */
    tags: z.array(z.string().min(1).max(40)).max(8).default([]),
    /** What it actually is, which beats guessing from duration alone. */
    kind: AudioKind,
    /** Music only: up to three genres and three moods from the fixed lists; empty for an effect. */
    genres: z.array(z.enum(MUSIC_GENRES)).max(3).default([]),
    moods: z.array(z.enum(MUSIC_MOODS)).max(3).default([]),
    /** Effects only: its category and the setting it belongs in, from the fixed lists; null for music. */
    sfxCategory: z.enum(SFX_CATEGORIES).nullable().default(null),
    setting: z.enum(SFX_SETTINGS).nullable().default(null),
    /**
     * The sound stands on its own: no music, voice or room ambience under or around it. What decides
     * whether an effect cut from a clip is good enough to share with every tenant.
     */
    isolated: z.boolean().default(false),
});
export function audioDescriptionSchema() {
    return {
        type: 'object',
        required: ['name', 'description', 'tags', 'kind', 'genres', 'moods', 'isolated'],
        properties: {
            name: { type: 'string' },
            description: { type: 'string' },
            tags: { type: 'array', items: { type: 'string' } },
            kind: { type: 'string', enum: ['bed', 'track', 'sfx', 'voice'] },
            genres: { type: 'array', items: { type: 'string', enum: [...MUSIC_GENRES] } },
            moods: { type: 'array', items: { type: 'string', enum: [...MUSIC_MOODS] } },
            sfxCategory: { type: 'string', enum: [...SFX_CATEGORIES], nullable: true },
            setting: { type: 'string', enum: [...SFX_SETTINGS], nullable: true },
            isolated: { type: 'boolean' },
        },
    };
}
/**
 * The listening prompt. It is told what the folders already said, because a pack that filed a sound
 * under "Foley/Glass" has told us something the audio alone might not, and contradicting it helps
 * nobody. It is NOT told the filename: the filename is junk, which is why we are here.
 */
/** A person's description of a sound this short is a label, and the listen writes the sentence from it. */
export const PERSON_WORDS_ENRICH_MAX = 6;
/**
 * `told` is what a person who heard the sound says it is (Dax, 2026-09-28): it is the truth, so the
 * listen describes that and adds the words a searcher would type for it, rather than guessing again.
 */
export function audioDescriptionPrompt(tags, durationMs, seen = null, taken = [], told = null) {
    return `Listen to this ${(durationMs / 1000).toFixed(1)} second audio sample and describe it for a
sound library, in British English.
${tags.length ? `The folders it arrived in said: ${tags.join(', ')}.\n` : ''}${told ? `A person who listened to it says it is: "${told}". That is right: describe it in those terms, never as
something else.\n` : ''}${seen && !told ? `It was cut from a video, and whoever watched that moment said it was: "${seen}". A second of sound
alone is easy to mistake, so name what makes it from that unless you clearly hear something else.\n` : ''}${taken.length ? `Other sounds from the same pack are already called: ${taken.join('; ')}. Never reuse one of those names:
say what sets this one apart (its pitch, length, material, how it ends).\n` : ''}
- name: a short human file name, 3 to 6 plain words ("Egg cracking on steel", "Glass bottle clink").
  No file extension, no take number, no trailing full stop.
- description: one sentence saying what makes the sound and how it sounds, so someone searching plain
  words ("something cracking", "pouring over ice") would land on it.
${told ? `- tags: up to 8 single lowercase words someone might search for it by: what makes it, who or what does
  it, and the everyday synonyms for each (a giggle: giggle, laugh, laughter, woman, girl, female, happy).`
        : '- tags: 2 to 5 single lowercase words for the material, the action and the texture (glass, pour, crisp).'}
- kind: "sfx" for a single sound or short effect, "bed" for music with no vocal meant to play under a
  cut, "track" for a recognisable song or a clip of one, "voice" for speech meant to be heard as a
  voiceover (narration, a read script, somebody talking) with no music under it.
- For a VOICEOVER: description says what is said, in the speaker's own words where you can hear them.
  Leave genres, moods, sfxCategory and setting empty.
- For MUSIC (bed or track): genres, one to three that fit best; moods, one to three for the emotion or
  sentiment it carries. Leave sfxCategory and setting null.
- For a SOUND EFFECT: sfxCategory, the one category it belongs in; setting, the environment it sounds
  recorded in, or null when it has none (a synthetic whoosh). Leave genres and moods empty.
- isolated: true only when the sound stands cleanly on its own, with no music, speech or room ambience
  under, before or after it, so it could be dropped into any other video as it is. False when anything
  else can be heard in it.`;
}
/**
 * The Voiceover library's judge (Dax, 2026-09-29). A clip's spoken lines are heard in place, one listen
 * per clip covering all of them, and a line joins the library only when it could be laid over other
 * footage as it is. Each verdict comes back against the line's number in the list it was given.
 */
export const VoiceLineVerdicts = z.object({
    lines: z.array(z.object({ line: z.number().int().min(1), clean: z.boolean() })).default([]),
});
export function voiceLineVerdictSchema() {
    return {
        type: 'object',
        required: ['lines'],
        properties: {
            lines: { type: 'array', items: { type: 'object', required: ['line', 'clean'], properties: { line: { type: 'integer' }, clean: { type: 'boolean' } } } },
        },
    };
}
export function voiceLinesPrompt(lines) {
    const listed = lines.map((l, i) => `${i + 1}. ${(l.startMs / 1000).toFixed(2)} s to ${(l.endMs / 1000).toFixed(2)} s: "${l.text}"`).join('\n');
    return `Listen to this video's sound. These are its spoken lines, with where each is said:
${listed}

For each line, judge whether its sound is clean enough to reuse as a voiceover laid over other footage,
exactly as it is: the voice clear and close to the microphone, every word whole (nothing cut off at the
start or the end), no music under it, no other voice over it, and little room noise, wind or clatter.
Answer with every line's number and clean true only when all of that holds; false otherwise.`;
}
/**
 * The 100-per-second peaks reduced to a fixed number of bars for a card. The loudest sample in each
 * bucket wins, not the average: a waveform is read for where the hits are, and averaging flattens
 * exactly the transients a sound library is full of.
 */
export function downsamplePeaks(peaks, points = AUDIO_WAVEFORM_POINTS) {
    if (!peaks.length)
        return [];
    if (peaks.length <= points)
        return peaks.map((p) => Math.round(p * 1000) / 1000);
    const out = [];
    for (let i = 0; i < points; i++) {
        const from = Math.floor((i * peaks.length) / points);
        const to = Math.max(from + 1, Math.floor(((i + 1) * peaks.length) / points));
        let peak = 0;
        for (let j = from; j < to && j < peaks.length; j++)
            if (peaks[j] > peak)
                peak = peaks[j];
        out.push(Math.round(peak * 1000) / 1000);
    }
    return out;
}
/** Audio containers, so only a real extension is stripped: "MudSuction_BW.9408" ends in a code, not a type. */
const AUDIO_EXTENSIONS = new Set(['wav', 'mp3', 'm4a', 'aac', 'ogg', 'flac', 'aiff', 'aif', 'wma', 'opus']);
/** A token that is only a number, a date or a clock time: a take number, never a word. */
function isNumeric(token) {
    return /^\d+$/.test(token)
        || /^(19|20)\d{6}$/.test(token)
        || /^(19|20)\d{2}[-.]?\d{2}[-.]?\d{2}$/.test(token)
        || /^\d{1,2}[.:]\d{2}([.:]\d{2})?$/.test(token);
}
/**
 * A vendor's stamp rather than a word: EC08, MON01, or a code with digits inside it (q0VPK). Kept as a
 * tag, never in the name: somebody may well search "the BW pack", and throwing it away would lose that.
 * Capitals alone are NOT a stamp: packs shout their words (FOLEY_STEP, WHOOSH_FAST), so an all-capitals
 * word is a word unless a catalogue number follows it (`isStampBefore`, Dax 2026-09-25).
 */
function isCode(token) {
    if (/[A-Za-z]\d+[A-Za-z]/.test(token) && token.length <= 8)
        return true;
    if (token.length > 6 || token.length < 2)
        return false;
    return /^[A-Z]{1,4}\d{1,3}$/.test(token);
}
/** A short run of capitals directly before a catalogue number: BW.9408, OCP-1461-010, ODY-1733-83. */
function isStampBefore(token, next) {
    return /^[A-Z]{2,4}$/.test(token) && next !== undefined && /^\d{3,}$/.test(next);
}
/**
 * A musical key stamped on a sample (F#_Electricity01, Bbm Pad): a tag, never a word. Only a sharp, a
 * flat or a spelled-out mode makes it one, so a lone "A" in "A Bomb Drop" stays a word.
 */
function isMusicalKey(token) {
    return /^[A-G](#|♯|♭|b)(m|min|maj)?$/.test(token) || /^[A-G](min|maj)$/.test(token);
}
/**
 * A pack's catalogue number: capitals then a long run of digits, PE190701 or SDT2035201. Too long for
 * `isCode`'s stamps, and no word looks like it. Kept as a tag, like a stamp (2026-09-23).
 */
function isCatalogue(token) {
    return /^[A-Z]{1,4}\d{4,10}$/.test(token);
}
/** Long runs of hex or an id: machine noise, worth neither a word nor a tag. */
function isMachineId(token) {
    return /^[0-9a-f]{8,}$/i.test(token) || /^[0-9a-f]{8}-[0-9a-f]{4}/i.test(token);
}
/** "MudSuction" -> "Mud Suction", "XMLFile" -> "XML File". Runs of capitals stay together. */
function splitCamel(token) {
    return token
        .replace(/([a-z\d])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .split(' ')
        .filter(Boolean);
}
function titleCase(word) {
    return word.length <= 1 ? word.toUpperCase() : word[0].toUpperCase() + word.slice(1).toLowerCase();
}
/**
 * A sample's filename read as a name and its buried metadata. Sound libraries bury catalogue numbers,
 * vendor stamps and take numbers in filenames; the words are the name, and the stamps become tags so
 * nothing is lost and everything stays searchable (Dax, 2026-09-20).
 *
 * Deterministic and cheap: this is rung zero of the naming ladder, below the fingerprint and the
 * listening pass, and it runs on every import whatever the file is called.
 */
export function cleanAudioName(filename) {
    const dot = filename.lastIndexOf('.');
    const base = dot > 0 && AUDIO_EXTENSIONS.has(filename.slice(dot + 1).toLowerCase()) ? filename.slice(0, dot) : filename;
    const words = [];
    const tags = [];
    const tag = (t) => {
        if (!tags.includes(t.toLowerCase()))
            tags.push(t.toLowerCase());
    };
    // A key keeps its sharp only until the split below, so it is read off the coarse pieces first.
    const rough = base.split(/[\s_\-.,()[\]]+/).filter(Boolean).flatMap((piece) => {
        if (!isMusicalKey(piece))
            return piece.split(/[^A-Za-z0-9]+/).filter(Boolean);
        tag(`key ${piece}`);
        return [];
    });
    for (const [i, token] of rough.entries()) {
        if (isMachineId(token) || isNumeric(token))
            continue;
        if (isCode(token) || isCatalogue(token) || isStampBefore(token, rough[i + 1])) {
            tag(token);
            continue;
        }
        const parts = splitCamel(token);
        // A vendor glues its prefix to the last word before the catalogue number: "BicycleBellRingsSm
        // PE190701" is the words Bicycle Bell Rings, the prefix SM and the number (2026-09-23).
        const next = rough[i + 1];
        if (parts.length > 1 && next !== undefined && isCatalogue(next) && /^[A-Z][a-z]?$/.test(parts[parts.length - 1]))
            tag(parts.pop());
        for (const part of parts) {
            if (isNumeric(part))
                continue;
            // A take number glued to its word: "Electricity01" is Electricity, take 01.
            const glued = /^([A-Za-z]{3,})\d{1,3}$/.exec(part);
            const word = glued ? glued[1] : part;
            // Shouted words read in title case; an acronym (TNT, VCR, DJ: short, no vowel) stays as it is.
            words.push(/^[B-DF-HJ-NP-TV-Z]{2,4}$/.test(word) ? word : titleCase(word));
        }
    }
    // Everything was numbers and stamps: the original is a poor name, but it beats an empty one.
    return { name: words.join(' ') || base.trim() || filename, tags };
}
/**
 * Names for a set of samples, numbered only where they collide. A folder of "cartoon sound effect 1"
 * to "…40" all clean to one name, so they become Cartoon Sound Effect 01 to 40; a name held by one
 * sample is left alone. Ordering is by the original filename, so running this again changes nothing.
 */
export function numberAudioNames(items, startAt = {}) {
    const groups = new Map();
    for (const item of items) {
        const key = item.name.toLowerCase();
        const group = groups.get(key);
        if (group)
            group.push(item);
        else
            groups.set(key, [item]);
    }
    const out = new Map();
    for (const [key, group] of groups) {
        const offset = startAt[key] ?? 0;
        if (group.length === 1 && !offset) {
            out.set(group[0].originalFilename, group[0].name);
            continue;
        }
        const ordered = [...group].sort((a, b) => a.originalFilename.localeCompare(b.originalFilename, 'en', { numeric: true, sensitivity: 'base' }));
        for (const [i, item] of ordered.entries()) {
            out.set(item.originalFilename, `${item.name} ${String(offset + i + 1).padStart(2, '0')}`);
        }
    }
    return out;
}
/** "Cartoon Sound Effect 01" -> "Cartoon Sound Effect": the name without the number that separates siblings. */
export function nameStem(name) {
    return name.replace(/\s+\d{2,}$/, '').trim();
}
/**
 * Words that describe the file rather than the sound. A name built from these announces that it is a
 * sound effect without saying which one.
 */
const GENERIC_WORDS = new Set(['sound', 'sounds', 'effect', 'effects', 'sfx', 'fx', 'audio', 'sample', 'samples', 'untitled', 'misc', 'various', 'unknown', 'unnamed']);
/**
 * Whether a sample is worth listening to. A junk filename is the obvious case; the other is a name
 * made of words that describe the file rather than the sound (Dax, 2026-09-20).
 *
 * Repetition alone does not buy forty listens: files called "Baby Cry 01" to "…40" repeat, but anyone
 * reading one knows what it is. It does buy an investigation (Dax, 2026-09-23): see `siblingGroups`
 * and `nameMatchesHearing`, which test one sibling and let the verdict stand for the rest.
 */
export function nameSaysNothing(name, junk) {
    if (junk)
        return true;
    return nameStem(name).toLowerCase().split(/[^a-z]+/).filter(Boolean).some((w) => GENERIC_WORDS.has(w));
}
/** Siblings: at least this many files whose names differ only by numbers, dates and stamps. */
export const SIBLING_GROUP_MIN = 2;
/** What siblings share: the cleaned name, so "Baby Cry 01.wav" and "Baby_Cry_40.wav" are both "baby cry". */
export function siblingKey(filename) {
    return cleanAudioName(filename).name.toLowerCase();
}
/**
 * Files that repeat one name with only numbers and stamps between them, keyed by what they share. A
 * repeated name is never trusted blind (Dax, 2026-09-23): music siblings are fingerprinted, and any other
 * group has one member listened to, whose verdict stands for the rest (`nameMatchesHearing`).
 */
export function siblingGroups(rows) {
    const groups = new Map();
    for (const row of rows) {
        const key = siblingKey(row.originalFilename ?? row.name);
        groups.set(key, [...(groups.get(key) ?? []), row]);
    }
    for (const [key, members] of groups)
        if (members.length < SIBLING_GROUP_MIN)
            groups.delete(key);
    return groups;
}
/**
 * Whether what the AI heard in one sibling bears the group's name out: the name's content words turn up
 * in what it heard (all of them for a two-word name, as the brand-line matcher requires). "Baby Cry" and
 * "a baby crying" agree; "Baby Cry" and "a door slamming shut" do not, and then the name is misleading
 * and every sibling earns its own listen.
 */
export function nameMatchesHearing(name, heard) {
    return matchScore([heard.name ?? '', heard.description ?? '', ...heard.tags].join(' '), nameStem(name)) > 0;
}
/**
 * The highest number already taken under a base name: "Mountain Bird 03" among the names gives 3, so a
 * rename carries on from 04. Case is ignored; "Mountain Birds 07" and a bare "Mountain Bird" take none.
 */
export function highestNumber(base, names) {
    const stem = `${base.trim().toLowerCase()} `;
    let highest = 0;
    for (const name of names) {
        const lower = name.trim().toLowerCase();
        if (!lower.startsWith(stem))
            continue;
        const rest = lower.slice(stem.length);
        if (/^\d+$/.test(rest))
            highest = Math.max(highest, Number(rest));
    }
    return highest;
}
/**
 * One name given to several chosen samples, in the order they were shown: "Mountain Bird" for three
 * becomes Mountain Bird 01 to 03, numbered as the naming ladder numbers a folder, carrying on after
 * `after` when the brand already holds numbered ones. A single new sample keeps the name bare, and a
 * hundred or more widen the number so every name sorts in order.
 */
export function numberedNames(base, count, after = 0) {
    const name = base.trim();
    if (count <= 0)
        return [];
    if (count === 1 && after === 0)
        return [name];
    const width = Math.max(2, String(after + count).length);
    return Array.from({ length: count }, (_, i) => `${name} ${String(after + i + 1).padStart(width, '0')}`);
}
//# sourceMappingURL=audio.js.map